import {
  BREAK,
  DefinitionNode,
  DocumentNode,
  Location,
  NamedTypeNode,
  TypeDefinitionNode,
  visit,
} from 'graphql';
import * as monacoEditor from 'monaco-editor';

import { API_SCHEMA_ID } from '../SDLDisplay';
import { uriToServiceName } from '../uriToServiceName';

const TYPE_DEFINITION_KINDS: Set<TypeDefinitionNode['kind']> = new Set([
  'ScalarTypeDefinition',
  'ObjectTypeDefinition',
  'InterfaceTypeDefinition',
  'UnionTypeDefinition',
  'EnumTypeDefinition',
  'InputObjectTypeDefinition',
] as const);
function isTypeDefinition(
  definition: DefinitionNode,
): definition is TypeDefinitionNode {
  return TYPE_DEFINITION_KINDS.has(
    definition.kind as TypeDefinitionNode['kind'],
  );
}

function rangeForLoc(loc: Location, model: monacoEditor.editor.ITextModel) {
  return monacoEditor.Range.fromPositions(
    model.getPositionAt(loc.start),
    model.getPositionAt(loc.end),
  );
}

function locationForNode(
  node: TypeDefinitionNode,
  model: monacoEditor.editor.ITextModel,
) {
  return node.loc
    ? {
        uri: model.uri,
        range: rangeForLoc(node.loc, model),
      }
    : [];
}

export async function getReferencesForPosition(
  model: monacoEditor.editor.ITextModel,
  position: monacoEditor.Position,
  providerStateRef: React.RefObject<{
    schemaName: string;
    documentNode: DocumentNode;
    allSchemas: Record<
      string,
      {
        model: monacoEditor.editor.ITextModel;
        documentNode: DocumentNode | undefined;
      }
    >;
  }>,
): Promise<null | monacoEditor.languages.Location[]> {
  if (!providerStateRef.current) {
    return null;
  }
  const { documentNode, allSchemas } = providerStateRef.current;
  let finalNodeMutable: NamedTypeNode | undefined;
  visit(documentNode, {
    enter(node): void | false | typeof BREAK {
      if (
        node.loc &&
        !rangeForLoc(node.loc, model).containsPosition(position)
      ) {
        return false;
      }
      if (node.kind === 'NamedType') {
        finalNodeMutable = node;
        return BREAK;
      }
    },
  });
  const finalNode = finalNodeMutable;

  switch (finalNode?.kind) {
    case undefined: {
      return null;
    }
    case 'NamedType': {
      return Object.values(allSchemas).flatMap(
        ({ documentNode: { definitions } = {}, model: otherModel }) =>
          definitions?.flatMap((definition) => {
            if (
              isTypeDefinition(definition) &&
              definition.name.value === finalNode.name.value &&
              // If the current file is not the Surface schema, don't show
              // results from the surface schema
              !(
                uriToServiceName(model.uri) !== API_SCHEMA_ID &&
                uriToServiceName(otherModel.uri) === API_SCHEMA_ID
              )
              // TODO understand federation better, why is this not required?
              // (definition.schemaName === schemaName ||
              //   definition.directives?.find(
              //     (directive) => directive.name.value === 'key',
              //   ))
            ) {
              return locationForNode(definition, otherModel);
            }
            return [];
          }) ?? [],
      );
    }
    default: {
      // TODO assertUnreachable(finalNode);
      return [];
    }
  }
}
